import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import "../styles/globalCss.css";

function Orders() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // فعلاً فقط در Frontend نگهداری می‌شود.
  // بعداً با API سیستم حسابداری جایگزین خواهد شد.
  const [accountingChecked, setAccountingChecked] = useState({});

  useEffect(() => {
    fetchOrders();
  }, [pageNumber]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get("/Order/report", {
        params: {
          includeLedger: true,
          includeProviderDetails: true,
          pageNumber,
          pageSize,
        },
      });

      const result = response.data?.data;

      setOrders(result?.orders || []);
      setTotalCount(result?.totalCount || 0);
      setTotalPages(result?.totalPages || 0);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setError("خطا در دریافت معاملات");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      alert("Id کپی شد.");
    } catch (err) {
      console.error("Copy failed:", err);
      alert("خطا در کپی");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return new Intl.DateTimeFormat("fa-IR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  const formatNumber = (value, maximumFractionDigits = 8) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const number = Number(value);

    if (Number.isNaN(number)) {
      return "-";
    }

    return number.toLocaleString("fa-IR", {
      maximumFractionDigits,
    });
  };

  const getSideText = (side) => {
    switch (side) {
      case "Buy":
        return "خرید";

      case "Sell":
        return "فروش";

      default:
        return side || "-";
    }
  };

  const getSideClass = (side) => {
    switch (side) {
      case "Buy":
        return "go-side-buy";

      case "Sell":
        return "go-side-sell";

      default:
        return "go-side-default";
    }
  };

  const getDebitCredit = (side) => {
    if (side === "Buy") {
      return "بدهکار";
    }

    if (side === "Sell") {
      return "بستانکار";
    }

    return "-";
  };

  const getDebitCreditClass = (side) => {
    if (side === "Buy") {
      return "go-debit";
    }

    if (side === "Sell") {
      return "go-credit";
    }

    return "go-debit-default";
  };

  const getStatusText = (status) => {
    const statusMap = {
      ProviderSucced: "موفق",
      ProviderFailed: "ناموفق",
      PendingProvider: "در انتظار تأمین‌کننده",
      Completed: "تکمیل شده",
      Failed: "ناموفق",
    };

    return statusMap[status] || status || "-";
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "ProviderSucced":
      case "Completed":
        return "go-status-success";

      case "ProviderFailed":
      case "Failed":
        return "go-status-danger";

      case "PendingProvider":
        return "go-status-pending";

      default:
        return "go-status-default";
    }
  };

  const toggleAccounting = (rowId) => {
    if (!rowId) return;

    setAccountingChecked((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }));
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === pageNumber) {
      return;
    }

    setPageNumber(page);
  };

  const renderAccountingToggle = (rowId) => {
    const checked = !!accountingChecked[rowId];

    return (
      <button
        type="button"
        className={`go-accounting-toggle ${
          checked ? "is-checked" : "is-unchecked"
        }`}
        onClick={() => toggleAccounting(rowId)}
        title={
          checked
            ? "علامت ثبت حسابداری برداشته شود"
            : "به عنوان ثبت‌شده در سیستم حسابداری علامت بزن"
        }
      >
        <span className="go-toggle-dot" />
        <span>{checked ? "ثبت شده" : "ثبت نشده"}</span>
      </button>
    );
  };

  const renderCopyId = (id, parent = false) => {
    if (!id) {
      return "-";
    }

    return (
      <div className="go-id-cell">
        <span className="go-id-text" title={id}>
          {id.slice(0, 8)}...
        </span>

        <button
          type="button"
          className="go-copy-btn"
          onClick={() => copyToClipboard(id)}
          title="کپی Id"
        >
          📋
        </button>
      </div>
    );
  };

  const renderMainOrderRow = (order, index) => {
    const isProviderSuccess = order.status === "ProviderSucced";

    return (
      <tr className="go-main-row" key={order.id}>
        <td className="go-index-cell">
          {(pageNumber - 1) * pageSize + index + 1}
        </td>

        <td>{renderCopyId(order.id)}</td>

        <td className="go-date-cell">
          {formatDate(order.createdDate)}
        </td>

        <td className="go-time-cell">
          {formatTime(order.createdDate)}
        </td>

        <td className="go-customer-cell">
          {order.customerFullName || "-"}
        </td>

        <td>
          <span className={`go-side ${getSideClass(order.side)}`}>
            {getSideText(order.side)}
          </span>
        </td>

        <td className="go-number-cell">
          {formatNumber(order.goldWight)}
        </td>

        <td className="go-number-cell go-price-cell">
          {formatNumber(order.price)}
        </td>

        <td className="go-number-cell">
          {formatNumber(order.totalAmount, 2)}
        </td>

        <td>
          <span
            className={`go-status ${getStatusClass(order.status)}`}
          >
            {getStatusText(order.status)}
          </span>
        </td>

        <td>
          <span
            className={`go-debit-credit ${getDebitCreditClass(
              order.side
            )}`}
          >
            {getDebitCredit(order.side)}
          </span>
        </td>

        <td>
          {order.providerOrder?.id ? (
            renderCopyId(order.providerOrder.id)
          ) : (
            <span className="go-empty-value">-</span>
          )}
        </td>

        <td>
          {renderAccountingToggle(order.id)}
        </td>
      </tr>
    );
  };

  const renderProviderOrderRow = (order) => {
    const providerOrder = order.providerOrder;

    if (
      order.status !== "ProviderSucced" ||
      !providerOrder
    ) {
      return null;
    }

    return (
      <tr
        className="go-provider-row"
        key={`${order.id}-provider`}
      >
        <td className="go-provider-index">
          <span className="go-provider-arrow">↳</span>
        </td>

        <td>
          <div className="go-provider-label">
            <span className="go-provider-label-dot" />
            معامله پایاپای
          </div>

          {renderCopyId(providerOrder.id)}
        </td>

        <td className="go-date-cell">
          {formatDate(providerOrder.createdDate)}
        </td>

        <td className="go-time-cell">
          {formatTime(providerOrder.createdDate)}
        </td>

        <td className="go-provider-name">
          {providerOrder.provider || "-"}
        </td>

        <td>
          <span className={`go-side ${getSideClass(order.side)}`}>
            {getSideText(order.side)}
          </span>
        </td>

        <td className="go-number-cell">
          {formatNumber(order.goldWight)}
        </td>

        <td className="go-number-cell go-price-cell">
          {formatNumber(
            providerOrder.totalPriceWithMaxCustomDiscount
          )}
        </td>

        <td className="go-number-cell">
          {formatNumber(providerOrder.expectedTotalAmount, 2)}
        </td>

        <td>
          <span
            className={`go-status ${getStatusClass(
              providerOrder.status
            )}`}
          >
            {getStatusText(providerOrder.status)}
          </span>
        </td>

        <td>
          <span
            className={`go-debit-credit ${getDebitCreditClass(
              order.side
            )}`}
          >
            {getDebitCredit(order.side)}
          </span>
        </td>

        <td>
          {renderCopyId(order.id, true)}
        </td>

        <td>
          {renderAccountingToggle(providerOrder.id)}
        </td>
      </tr>
    );
  };

  return (
    <div className="go-page" dir="rtl">
      <div className="go-wrap">
        {/* Header */}
        <header className="go-header">
          <div className="go-header-top">
            <button
              type="button"
              className="go-back"
              onClick={() => navigate("/dashboard")}
            >
              ← بازگشت به داشبورد
            </button>

            <button
              type="button"
              className="go-refresh"
              onClick={fetchOrders}
              disabled={loading}
            >
              <span className={loading ? "go-spin" : ""}>
                ↻
              </span>

              {loading ? "در حال دریافت..." : "بروزرسانی"}
            </button>
          </div>

          <div className="go-title-area">
            <div className="go-eyebrow">
              مدیریت معاملات
            </div>

            <h1 className="go-title">
              معاملات
            </h1>

            <p className="go-subtitle">
              مشاهده و نظارت بر سفارش‌ها و معاملات ثبت‌شده در سامانه
            </p>
          </div>
        </header>

        {/* Error */}
        {error && (
          <div className="go-error">
            {error}
          </div>
        )}

        {/* Summary */}
        <div className="go-summary">
          <div className="go-summary-item">
            <span className="go-summary-label">
              تعداد معاملات
            </span>

            <strong className="go-summary-value">
              {formatNumber(totalCount, 0)}
            </strong>
          </div>

          <div className="go-summary-divider" />

          <div className="go-summary-item">
            <span className="go-summary-label">
              صفحه
            </span>

            <strong className="go-summary-value">
              {formatNumber(pageNumber, 0)}
              {totalPages > 0 && ` از ${formatNumber(totalPages, 0)}`}
            </strong>
          </div>

          <div className="go-summary-divider" />

          <div className="go-summary-item go-summary-info">
            <span className="go-info-dot" />
            <span>
              معاملات موفق دارای اطلاعات معامله پایاپای هستند
            </span>
          </div>
        </div>

        {/* Table */}
        <section className="go-table-card">
          <div className="go-table-header">
            <div>
              <h2>لیست معاملات</h2>
              <p>
                جزئیات سفارش و اطلاعات تأمین‌کننده
              </p>
            </div>
          </div>

          <div className="go-table-scroll">
            <table className="go-table">
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>Id</th>
                  <th>تاریخ</th>
                  <th>ساعت</th>
                  <th>نام مشتری</th>
                  <th>ساید</th>
                  <th>وزن معامله</th>
                  <th>مظنه مثقال</th>
                  <th>مبلغ معامله</th>
                  <th>وضعیت معامله</th>
                  <th>بدهکار / بستانکار</th>
                  <th>معامله پایاپای</th>
                  <th>ثبت در حسابداری</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="13" className="go-loading">
                      <span className="go-loading-spinner" />
                      در حال دریافت معاملات...
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="go-empty">
                      <div className="go-empty-icon">
                        💰
                      </div>

                      <div>
                        <strong>معامله‌ای یافت نشد</strong>
                        <span>
                          در حال حاضر اطلاعاتی برای نمایش وجود ندارد.
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  orders.map((order, index) => (
                    <React.Fragment key={order.id}>
                      {renderMainOrderRow(order, index)}
                      {renderProviderOrderRow(order)}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="go-pagination">
              <button
                type="button"
                className="go-page-btn"
                onClick={() => goToPage(pageNumber - 1)}
                disabled={pageNumber <= 1 || loading}
              >
                → قبلی
              </button>

              <div className="go-page-numbers">
                {Array.from(
                  { length: totalPages },
                  (_, index) => index + 1
                )
                  .filter((page) => {
                    if (totalPages <= 7) {
                      return true;
                    }

                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - pageNumber) <= 1
                    );
                  })
                  .map((page, index, visiblePages) => {
                    const previousPage =
                      visiblePages[index - 1];

                    const showDots =
                      previousPage &&
                      page - previousPage > 1;

                    return (
                      <React.Fragment key={page}>
                        {showDots && (
                          <span className="go-page-dots">
                            ...
                          </span>
                        )}

                        <button
                          type="button"
                          className={`go-page-number ${
                            page === pageNumber
                              ? "active"
                              : ""
                          }`}
                          onClick={() => goToPage(page)}
                          disabled={loading}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <button
                type="button"
                className="go-page-btn"
                onClick={() => goToPage(pageNumber + 1)}
                disabled={
                  pageNumber >= totalPages || loading
                }
              >
                بعدی ←
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Orders;